import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { 
  User, 
  LoginCredentials, 
  CreateUserData, 
  UpdateUserData, 
  AuthResponse,
  ApiResponse 
} from '../types';

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3000',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add token to requests if available
    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Handle token expiration
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Public methods for other services to use
  async get(url: string) {
    return this.api.get(url);
  }

  async post(url: string, data?: any) {
    return this.api.post(url, data);
  }

  async patch(url: string, data?: any) {
    return this.api.patch(url, data);
  }

  async delete(url: string) {
    return this.api.delete(url);
  }

  async put(url: string, data?: any) {
    return this.api.put(url, data);
  }

  // Authentication
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response: AxiosResponse<AuthResponse> = await this.api.post('/auth/login', credentials);
    return response.data;
  }

  async logout(): Promise<ApiResponse> {
    const response: AxiosResponse<ApiResponse> = await this.api.post('/auth/logout');
    return response.data;
  }

  async forgotPassword(email: string): Promise<ApiResponse> {
    const response: AxiosResponse<ApiResponse> = await this.api.post('/auth/forgot-password', { email });
    return response.data;
  }

  async resetPassword(token: string, newPassword: string): Promise<ApiResponse> {
    const response: AxiosResponse<ApiResponse> = await this.api.post('/auth/reset-password', {
      token,
      newPassword,
    });
    return response.data;
  }

  async getCurrentUser(): Promise<{ user: any }> {
    const response: AxiosResponse<{ user: any }> = await this.api.post('/auth/me');
    return response.data;
  }

  // Users
  async getUsers(): Promise<User[]> {
    console.log('API Service: Getting all users');
    const response: AxiosResponse<User[]> = await this.api.get('/users');
    console.log('API Service: Users response:', response.data);
    return response.data;
  }

  async getUser(id: string): Promise<User> {
    console.log('API Service: Getting user with ID:', id);
    if (!id || id === 'undefined') {
      throw new Error('Invalid user ID provided to getUser');
    }
    const response: AxiosResponse<User> = await this.api.get(`/users/${id}`);
    return response.data;
  }

  async createUser(userData: CreateUserData): Promise<User> {
    console.log('API Service: Creating user with data:', userData);
    const response: AxiosResponse<User> = await this.api.post('/users', userData);
    console.log('API Service: Created user response:', response.data);
    return response.data;
  }

  async updateUser(id: string, userData: UpdateUserData): Promise<User> {
    console.log('API Service: Updating user with ID:', id, 'Data:', userData);
    if (!id || id === 'undefined') {
      throw new Error('Invalid user ID provided to updateUser');
    }
    const response: AxiosResponse<User> = await this.api.patch(`/users/${id}`, userData);
    console.log('API Service: Updated user response:', response.data);
    return response.data;
  }

  async deleteUser(id: string): Promise<void> {
    console.log('API Service: Deleting user with ID:', id);
    if (!id || id === 'undefined') {
      throw new Error('Invalid user ID provided to deleteUser');
    }
    await this.api.delete(`/users/${id}`);
  }

  async activateUser(id: string): Promise<User> {
    console.log('API Service: Activating user with ID:', id);
    if (!id || id === 'undefined') {
      throw new Error('Invalid user ID provided to activateUser');
    }
    const response: AxiosResponse<User> = await this.api.patch(`/users/${id}/activate`);
    return response.data;
  }

  async deactivateUser(id: string): Promise<User> {
    console.log('API Service: Deactivating user with ID:', id);
    if (!id || id === 'undefined') {
      throw new Error('Invalid user ID provided to deactivateUser');
    }
    const response: AxiosResponse<User> = await this.api.patch(`/users/${id}/deactivate`);
    return response.data;
  }

  async getUserProfile(): Promise<User> {
    console.log('API Service: Getting user profile');
    const response: AxiosResponse<User> = await this.api.get('/users/profile');
    console.log('API Service: Profile response:', response.data);
    return response.data;
  }
}

export const apiService = new ApiService(); 